/* =====================================================================================
   Leave and absence. Everything goes through app.* security-definer functions because the
   underlying tables live in the walled hr schema: sickness is health data, and colleagues
   see when someone is away, never why.
   ===================================================================================== */

import { BACKEND } from './db';
import { explain } from './dberrors';

const live = () => BACKEND === 'supabase';

export const LEAVE_KINDS = [
  ['annual', 'Annual leave'],
  ['sick', 'Sickness'],
  ['unpaid', 'Unpaid leave'],
  ['compassionate', 'Compassionate leave'],
  ['parental', 'Parental leave'],
  ['jury', 'Jury service'],
  ['other', 'Other'],
] as const;

export interface LeaveSummary {
  out_year_start: string; out_allowance: number; out_taken: number;
  out_booked: number; out_pending: number; out_remaining: number;
  out_adjustments: number;
}
export interface LeaveRow {
  out_id: string; out_kind: string; out_starts: string; out_ends: string;
  out_days: number; out_status: string; out_reason: string | null;
  out_decision_note: string | null; out_incident: string | null;
  out_fit_note: boolean; out_rtw_done: boolean;
}
export interface ApprovalRow {
  out_id: string; out_who: string; out_kind: string; out_starts: string; out_ends: string;
  out_days: number; out_reason: string | null; out_requested_at: string;
  out_fit_note: boolean;
}
export interface AwayRow { out_who: string; out_starts: string; out_ends: string; out_kind: string }

async function rpc<T>(fn: string, args?: Record<string, unknown>): Promise<T> {
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().schema('app').rpc(fn, args as never);
  if (error) throw new Error(explain(error.message, 'talk to the leave register'));
  return data as T;
}

export const fetchLeaveSummary = async (): Promise<LeaveSummary | null> =>
  live() ? ((await rpc<LeaveSummary[]>('my_leave_summary'))?.[0] ?? null) : null;
export const fetchMyLeave = async (): Promise<LeaveRow[]> =>
  live() ? await rpc<LeaveRow[]>('my_leave') : [];
export const fetchLeaveApprovals = async (): Promise<ApprovalRow[]> =>
  live() ? await rpc<ApprovalRow[]>('leave_approvals') : [];
export const fetchTeamAway = async (from?: string, to?: string): Promise<AwayRow[]> =>
  live() ? await rpc<AwayRow[]>('team_away', { p_from: from, p_to: to }) : [];

export const requestLeave = (r: { kind: string; start: string; end: string;
  startHalf?: boolean; endHalf?: boolean; reason?: string }) =>
  rpc<string>('request_leave', { p_kind: r.kind, p_start: r.start, p_end: r.end,
    p_start_half: r.startHalf ?? false, p_end_half: r.endHalf ?? false,
    p_reason: r.reason || null, p_for_member: null });

export const decideLeave = (id: string, approve: boolean, note?: string) =>
  rpc<void>('decide_leave', { p_id: id, p_approve: approve, p_note: note || null });

export const cancelLeave = (id: string) => rpc<void>('cancel_leave', { p_id: id });
